import CycleMenu from './cycle-menu';
import Meal from './meal';
import MealCategory, {
  type MealCategoryWithoutRelationships,
  toMealCategoryWithoutRelationships,
} from './meal-category';
import {
  BaseEntity,
  JsonApiProperty,
  JsonApiRelationship,
  JsonApiResource,
} from '../jsonapi-provider';
import { type ServeType } from '../types';
import { LocalDate } from '../utils/local-date';
import type MealItem from './meal-item';

@JsonApiResource({
  type: 'scheduled-meal',
})
export default class ScheduledMeal extends BaseEntity {
  @JsonApiProperty({ resolver: (val: string) => new LocalDate(val) }) day = new LocalDate();
  @JsonApiProperty({ resolver: Number }) price: number = 0.0;
  isException: boolean = false;
  name: string = '';
  mealItemsNames: string[] = [];
  extraMealItemServeType1: ServeType | null = null;
  extraMealItemServeType2: ServeType | null = null;
  @JsonApiRelationship('meal') meal = new Meal();
  @JsonApiRelationship('meal-category') mealCategory = new MealCategory();
  @JsonApiRelationship('meal-item') extraMealItem1: MealItem | null = null;
  @JsonApiRelationship('meal-item') extraMealItem2: MealItem | null = null;
  @JsonApiRelationship('cycle-menu') cycleMenu = new CycleMenu();

  constructor(
    id?: string,
    day = new LocalDate(),
    meal = new Meal(),
    mealCategory = new MealCategory(),
    extraMealItemServeType1: ServeType | null = null,
    extraMealItemServeType2: ServeType | null = null
  ) {
    super();
    this.id = id;
    this.day = day;
    this.meal = meal;
    this.mealCategory = mealCategory;
    this.extraMealItemServeType1 = extraMealItemServeType1;
    this.extraMealItemServeType2 = extraMealItemServeType2;
  }
}

export interface ScheduledMealWithoutCycleMenu
  extends Omit<
    ScheduledMeal,
    | 'cycleMenu'
    | 'mealCategory'
    | 'meal'
    | 'extraMealItem1'
    | 'extraMealItem2'
    | 'extraMealItemServeType1'
    | 'extraMealItemServeType2'
  > {
  mealCategory: MealCategoryWithoutRelationships;
  extraMealItem1?: Pick<MealItem, 'id' | 'name'>;
  extraMealItem2?: Pick<MealItem, 'id' | 'name'>;
  extraMealItemServeType1?: string;
  extraMealItemServeType2?: string;
}

export const toScheduledMealWithoutCycleMenu = (
  scheduledMeal: ScheduledMeal
): ScheduledMealWithoutCycleMenu => {
  const extraMealItems: {
    extraMealItem1?: Pick<MealItem, 'id' | 'name'>;
    extraMealItem2?: Pick<MealItem, 'id' | 'name'>;
    extraMealItemServeType1?: ServeType;
    extraMealItemServeType2?: ServeType;
  } = {};
  if (scheduledMeal.extraMealItem1) {
    extraMealItems.extraMealItem1 = {
      id: scheduledMeal.extraMealItem1.id,
      name: scheduledMeal.extraMealItem1.name,
    };
  }
  if (scheduledMeal.extraMealItem2) {
    extraMealItems.extraMealItem2 = {
      id: scheduledMeal.extraMealItem2.id,
      name: scheduledMeal.extraMealItem2.name,
    };
  }
  if (scheduledMeal.extraMealItemServeType1) {
    extraMealItems.extraMealItemServeType1 = scheduledMeal.extraMealItemServeType1;
  }
  if (scheduledMeal.extraMealItemServeType2) {
    extraMealItems.extraMealItemServeType2 = scheduledMeal.extraMealItemServeType2;
  }

  return {
    id: scheduledMeal?.id,
    day: scheduledMeal?.day,
    price: scheduledMeal?.price,
    isException: scheduledMeal?.isException,
    name: scheduledMeal?.name,
    mealItemsNames: scheduledMeal?.mealItemsNames,
    mealCategory: toMealCategoryWithoutRelationships(scheduledMeal?.mealCategory),
    ...extraMealItems,
  };
};
